import { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import { cls } from "../hatim/soft/SoftKit";
import { useToast } from "../../components/ui/Toast";
import { useScriptMode } from "../../contexts/ScriptModeContext";
import { useSendNotificationMutation, useListNotificationsQuery } from "../../api/notificationApi";
import { useSearchGroupsQuery } from "../../api/adminApi";
import { statusLabelKey } from "../../lib/notificationOptions";
import { formatDateTime, formatNumber } from "../../lib/adminFormat";
import {
  PageHeader,
  Section,
  NoData,
  ErrorRetry,
  SkeletonRows,
  ConfirmDialog,
  Tag,
  SearchInput,
  useDebouncedValue,
} from "./components/AdminKit";

const TITLE_MAX = 100;
const BODY_MAX = 500;

const STATUS_TONE = { done: "ok", failed: "danger", sending: "brand", pending: "neutral" };

function AudienceOption({ value, current, onChange, label, hint }) {
  const active = value === current;
  return (
    <label
      className={`flex-1 min-w-[140px] flex items-start gap-2.5 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${
        active
          ? "border-terakkiPrimaryDark bg-terakkiBrandCloud dark:border-[#8FC9CA] dark:bg-white/[0.08]"
          : "border-terakkiBorder hover:bg-terakkiSurfaceAlt dark:border-white/[0.14] dark:hover:bg-white/[0.05]"
      }`}
    >
      <input
        type="radio"
        name="audience"
        value={value}
        checked={active}
        onChange={() => onChange(value)}
        className="mt-1 accent-[#006A71]"
      />
      <span>
        <span className={`block text-base ${cls.title}`}>{label}</span>
        <span className={`block text-xs ${cls.muted}`}>{hint}</span>
      </span>
    </label>
  );
}

function GroupPicker({ selected, onAdd, onRemove }) {
  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q.trim());
  // Boş aramada sunucu ada göre ilk 20 grubu döner.
  const { data, isFetching } = useSearchGroupsQuery({ q: debouncedQ });
  const results = (data?.groups || []).filter((g) => !selected.some((s) => s.id === g.id));

  return (
    <div className="flex flex-col gap-2">
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((g) => (
            <span
              key={g.id}
              className={`inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full text-sm ${cls.chip}`}
            >
              {g.name}
              <button
                type="button"
                onClick={() => onRemove(g.id)}
                aria-label={`${g.name} kaldır`}
                className="w-5 h-5 rounded-full flex items-center justify-center hover:bg-black/5 dark:hover:bg-white/10"
              >
                <CloseIcon sx={{ fontSize: 14 }} aria-hidden />
              </button>
            </span>
          ))}
        </div>
      )}
      <SearchInput value={q} onChange={setQ} placeholder="Grup adı ara" className="w-full" />
      <ul className={`rounded-lg border border-terakkiBorder dark:border-white/[0.14] max-h-56 overflow-y-auto ${isFetching ? "opacity-60" : ""}`}>
        {results.length === 0 ? (
          <li className={`px-3 py-2.5 text-sm ${cls.muted}`}>{isFetching ? "Aranıyor" : debouncedQ ? "Uyan grup yok" : "Grup yok"}</li>
        ) : (
          results.map((g) => (
            <li key={g.id} className={`${cls.row} last:border-b-0`}>
              <button
                type="button"
                onClick={() => onAdd(g)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left ${cls.rowHover}`}
              >
                <AddIcon sx={{ fontSize: 16 }} className={cls.accent} aria-hidden />
                <span className={`flex-1 min-w-0 truncate text-base ${cls.title}`}>{g.name}</span>
                <span className={`text-sm ${cls.muted}`}>{formatNumber(g.memberCount)} üye</span>
              </button>
            </li>
          ))
        )}
      </ul>
    </div>
  );
}

function Preview({ title, body }) {
  return (
    <div className="rounded-xl border border-terakkiBorder bg-terakkiSurfaceAlt p-3 flex gap-3 dark:border-white/[0.14] dark:bg-white/[0.05]">
      <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${cls.avatar}`}>
        <NotificationsNoneIcon sx={{ fontSize: 20 }} aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className={`text-base truncate ${cls.title}`}>{title || "Duyuru başlığı"}</span>
          <span className={`text-xs shrink-0 ${cls.faint}`}>şimdi</span>
        </div>
        <p className={`text-sm whitespace-pre-line break-words ${cls.muted}`}>{body || "Duyuru metni burada görünür."}</p>
      </div>
    </div>
  );
}

function ComposeForm() {
  const toast = useToast();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState("all");
  const [groups, setGroups] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [send, { isLoading }] = useSendNotificationMutation();

  const valid =
    title.trim().length > 0 &&
    body.trim().length > 0 &&
    (audience === "all" || groups.length > 0);

  const targetText =
    audience === "all" ? "Terakki'yi kullanan herkese" : `${groups.length} gruptaki üyelere`;

  const submit = async () => {
    const payloads =
      audience === "all"
        ? [{ audienceType: "all", title: title.trim(), body: body.trim() }]
        : groups.map((g) => ({ audienceType: "group", audienceRefId: g.id, title: title.trim(), body: body.trim() }));
    const results = await Promise.all(payloads.map((p) => send(p)));
    const failed = results.filter((r) => r.error);
    setConfirmOpen(false);
    if (failed.length === 0) {
      toast.success("Duyuru gönderime alındı.");
      setTitle("");
      setBody("");
      setGroups([]);
    } else if (failed.length === results.length) {
      toast.error(failed[0].error?.message || "Duyuru gönderilemedi.");
    } else {
      toast.warning(`${results.length - failed.length} gruba gönderildi, ${failed.length} grupta hata oluştu.`);
    }
  };

  const field =
    "w-full px-3 py-2 rounded-lg border border-terakkiBorder bg-terakkiSurface text-base text-terakkiInk outline-none focus:border-terakkiPrimary " +
    cls.input;

  return (
    <Section title="Yeni duyuru" subtitle="Uygulamaya bildirim olarak gider">
      <div className="flex flex-col gap-4">
        <label className="flex flex-col gap-1">
          <span className={`text-sm ${cls.muted}`}>Başlık</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value.slice(0, TITLE_MAX))}
            className={`${field} h-10`}
            placeholder="Kısa bir başlık"
          />
          <span className={`text-xs self-end ${cls.faint}`}>
            {title.length}/{TITLE_MAX}
          </span>
        </label>
        <label className="flex flex-col gap-1">
          <span className={`text-sm ${cls.muted}`}>Metin</span>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value.slice(0, BODY_MAX))}
            rows={4}
            className={`${field} resize-y`}
            placeholder="Duyuru metni"
          />
          <span className={`text-xs self-end ${cls.faint}`}>
            {body.length}/{BODY_MAX}
          </span>
        </label>

        <div className="flex flex-col gap-2">
          <span className={`text-sm ${cls.muted}`}>Hedef</span>
          <div className="flex flex-wrap gap-2">
            <AudienceOption value="all" current={audience} onChange={setAudience} label="Herkes" hint="Tüm kullanıcılar" />
            <AudienceOption value="group" current={audience} onChange={setAudience} label="Gruplar" hint="Seçilen grupların üyeleri" />
          </div>
          {audience === "group" && (
            <GroupPicker
              selected={groups}
              onAdd={(g) => setGroups((cur) => (cur.some((x) => x.id === g.id) ? cur : cur.concat(g)))}
              onRemove={(id) => setGroups((cur) => cur.filter((x) => x.id !== id))}
            />
          )}
        </div>

        <div className="flex flex-col gap-2">
          <span className={`text-sm ${cls.muted}`}>Önizleme</span>
          <Preview title={title.trim()} body={body.trim()} />
        </div>

        <div className={`flex items-center justify-between gap-3 pt-3 ${cls.footer}`}>
          <span className={`text-sm ${cls.muted}`}>{valid ? targetText : "Başlık, metin ve hedef gerekli"}</span>
          <button
            type="button"
            disabled={!valid || isLoading}
            onClick={() => setConfirmOpen(true)}
            className="h-10 px-5 rounded-full bg-terakkiPrimaryDark text-white text-base hover:bg-terakkiPrimary disabled:opacity-40 dark:bg-[#8FC9CA] dark:text-[#123B3D]"
          >
            Gönder
          </button>
        </div>
      </div>
      <ConfirmDialog
        open={confirmOpen}
        title="Duyuru gönderilsin mi?"
        text={`"${title.trim()}" başlıklı duyuru ${targetText} bildirim olarak gidecek. Gönderim geri alınamaz.`}
        confirmLabel="Gönder"
        busy={isLoading}
        onConfirm={submit}
        onClose={() => setConfirmOpen(false)}
      />
    </Section>
  );
}

function History() {
  const { t } = useScriptMode();
  const { data, isLoading, isError, error, refetch } = useListNotificationsQuery({ limit: 50 });
  const items = (data?.data?.notifications || []).filter((n) => (n.kind || "announcement") === "announcement");

  return (
    <Section title="Geçmiş duyurular" subtitle={data ? `${formatNumber(items.length)} duyuru` : undefined} bodyClassName="p-0">
      {isLoading ? (
        <SkeletonRows count={4} />
      ) : isError ? (
        <ErrorRetry message={error?.message} onRetry={refetch} error={error} />
      ) : items.length === 0 ? (
        <NoData text="Henüz duyuru yok" />
      ) : (
        <ul className="lg:max-h-[calc(100vh-220px)] lg:overflow-y-auto">
          {items.map((n) => {
            const status = n.status || "pending";
            const target = n.audience?.type === "all" ? "Herkes" : n.audience?.name || "Grup";
            return (
              <li key={n._id || n.id} className={`px-4 py-3 ${cls.row} last:border-b-0`}>
                <div className="flex items-start justify-between gap-3">
                  <p className={`text-base leading-snug min-w-0 ${cls.title}`}>{n.title}</p>
                  <Tag tone={STATUS_TONE[status]}>{t(statusLabelKey(status))}</Tag>
                </div>
                <p className={`text-sm mt-0.5 break-words ${cls.muted}`}>{n.body}</p>
                <p className={`text-xs mt-1.5 ${cls.faint}`}>
                  {target} · {formatNumber(n.stats?.recipients)} kişi · {formatNumber(n.stats?.sent)}/
                  {formatNumber(n.stats?.tokens)} cihaz
                  {n.stats?.failed > 0 ? ` · ${formatNumber(n.stats.failed)} hata` : ""} · {formatDateTime(n.createdAt)}
                </p>
                {n.error && <p className={`text-xs mt-1 break-all ${cls.danger}`}>{n.error}</p>}
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}

export default function AdminAnnouncementsPage() {
  return (
    <div className="max-w-7xl mx-auto flex flex-col gap-5 pb-8">
      <PageHeader title="Duyurular" subtitle="Tüm kullanıcılara ya da seçilen gruplara bildirim" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        <ComposeForm />
        <History />
      </div>
    </div>
  );
}
